import React from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { useCreateActivity } from '@/hooks';
import { Label } from '../../../components/Label';
import type { InputDefaultProps } from '../types';
import { FormItem } from '../../../components/FormItem';
import { SelectWithCreation } from '@/components/ui/Inputs';
import type { CreateActivityRequest } from '@/components/ui/Inputs/types/request';

const { useFormInstance, useWatch } = Form;

export function Activity({
	fieldName,
	isLoading = false,
	data = [],
	isError = false
}: Readonly<InputDefaultProps>): JSX.Element {
	const form = useFormInstance();
	const title = I18n.get('Activity');

	const organizationId = useWatch([fieldName, 'organization_id'], form);
	const companyId = useWatch([fieldName, 'company_id'], form);
	const workstationId = useWatch([fieldName, 'workstation_id'], form);

	const { mutateAsync: createActivity, isLoading: isCreatingActivity } = useCreateActivity();

	function handleSelectActivity(id: string): void {
		form.setFieldValue([fieldName, 'activity_id'], id);
	}

	function handleClearActivity(): void {
		form.setFieldValue([fieldName, 'activity_id'], undefined);
	}

	async function handleCreateActivity(name: string): Promise<void> {
		const body: CreateActivityRequest = {
			name,
			company_id: companyId,
			workstation_id: workstationId,
			organization_id: organizationId
		};

		const activity = await createActivity(body);
		const activityId = activity?.id;

		if (activityId) {
			handleSelectActivity(activityId);
		}
	}

	return (
		<Col xs={24}>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Label isRequired>{title}</Label>
				</Col>
				<Col span={24}>
					<FormItem
						name={[fieldName, 'activity_id']}
						rules={[{ required: true, message: I18n.get('Enter the activity') }]}
					>
						<SelectWithCreation
							showSearch
							allowClear
							size="large"
							allowCreation
							placeholder={title}
							loading={isLoading}
							selectOptions={data}
							onClear={handleClearActivity}
							onCreate={handleCreateActivity}
							onChange={handleSelectActivity}
							creatingData={isCreatingActivity}
							disabled={isLoading || isError}
						/>
					</FormItem>
				</Col>
			</Row>
		</Col>
	);
}
